// src/pages/deposit/Dashboard.jsx

import React, {
    useState,
    useEffect,
    useRef,
    useCallback
} from 'react';

import axios from 'axios';

import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';

import './Dashboard.css';

const API_BASE_URL =
    process.env.REACT_APP_API_PATH || 'http://localhost:8060/api/';

const DepositDashboard = () => {

    const toast = useRef(null);

    // =========================================================
    // STATE
    // =========================================================

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);

    const [statistics, setStatistics] = useState(null);
    const [trends, setTrends] = useState([]);
    const [branchData, setBranchData] = useState([]);
    const [insights, setInsights] = useState([]);
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [upcomingMaturities, setUpcomingMaturities] = useState([]);

    const [filterOptions, setFilterOptions] = useState({
        branches: [],
        years: []
    });

    const [accountSummary, setAccountSummary] = useState(null);
    const [searchMemberId, setSearchMemberId] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);

    const [showAccountDialog, setShowAccountDialog] = useState(false);
    const [showMaturityDialog, setShowMaturityDialog] = useState(false);

    const [activeTab, setActiveTab] = useState(0);

    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        branchCode: '',
        year: new Date().getFullYear()
    });

    // =========================================================
    // AUTH
    // =========================================================

    const getToken = () => {
        return (
            localStorage.getItem('token') ||
            sessionStorage.getItem('token')
        );
    };

    // =========================================================
    // API CALL
    // =========================================================

    const apiCall = useCallback(
        async (
            endpoint,
            method = 'GET',
            data = null,
            params = null
        ) => {

            const token = getToken();

            const config = {
                method,
                url: `${API_BASE_URL}${endpoint}`,
                headers: {
                    'Content-Type': 'application/json'
                }
            };

            // FIXED: use token, not undefined bearerToken
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            if (params) {
                const cleanedParams = {};

                Object.keys(params).forEach((key) => {

                    const value = params[key];

                    if (
                        value !== undefined &&
                        value !== null &&
                        value !== ''
                    ) {
                        cleanedParams[key] = value;
                    }
                });

                if (Object.keys(cleanedParams).length > 0) {
                    config.params = cleanedParams;
                }
            }

            if (data !== null && data !== undefined) {
                config.data = data;
            }

            const response = await axios(config);

            return response.data;
        },
        []
    );

    // =========================================================
    // FILTER OPTIONS
    // =========================================================

    const fetchFilterOptions = useCallback(async () => {

        try {

            const response =
                await apiCall('dashboard/filter-options');

            if (response?.success) {

                setFilterOptions(
                    response.data || {
                        branches: [],
                        years: []
                    }
                );

                return true;
            }

            return false;

        } catch (err) {

            console.error(
                'Filter options error:',
                err
            );

            setFilterOptions({
                branches: [],
                years: [
                    new Date().getFullYear(),
                    new Date().getFullYear() - 1
                ]
            });

            return false;
        }

    }, [apiCall]);

    // =========================================================
    // DASHBOARD DATA
    // =========================================================

    const fetchDashboardData = useCallback(async () => {

        setLoading(true);
        setError(null);

        try {

            const commonParams = {};

            if (filters.startDate) {
                commonParams.startDate =
                    filters.startDate.toISOString();
            }

            if (filters.endDate) {
                commonParams.endDate =
                    filters.endDate.toISOString();
            }

            if (
                filters.branchCode &&
                filters.branchCode.trim() !== ''
            ) {
                commonParams.branchCode =
                    filters.branchCode.trim();
            }

            const trendsParams = {
                year:
                    filters.year ||
                    new Date().getFullYear()
            };

            if (
                filters.branchCode &&
                filters.branchCode.trim() !== ''
            ) {
                trendsParams.branchCode =
                    filters.branchCode.trim();
            }

            const insightsParams = {};

            if (
                filters.branchCode &&
                filters.branchCode.trim() !== ''
            ) {
                insightsParams.branchCode =
                    filters.branchCode.trim();
            }

            const maturityParams = {
                days: 30
            };

            if (
                filters.branchCode &&
                filters.branchCode.trim() !== ''
            ) {
                maturityParams.branchCode =
                    filters.branchCode.trim();
            }

            /*
             * IMPORTANT
             *
             * These are the ONLY dashboard endpoints
             * currently implemented in DashboardController.
             */

            const results =
                await Promise.allSettled([

                    apiCall(
                        'dashboard/statistics',
                        'GET',
                        null,
                        commonParams
                    ),

                    apiCall(
                        'dashboard/trends',
                        'GET',
                        null,
                        trendsParams
                    ),

                    apiCall(
                        'dashboard/branchwise',
                        'GET',
                        null,
                        {
                            startDate:
                                filters.startDate
                                    ? filters.startDate.toISOString()
                                    : null,

                            endDate:
                                filters.endDate
                                    ? filters.endDate.toISOString()
                                    : null
                        }
                    ),

                    apiCall(
                        'dashboard/insights',
                        'GET',
                        null,
                        insightsParams
                    ),

                    apiCall(
                        'dashboard/recent-transactions',
                        'GET',
                        null,
                        {
                            limit: 10
                        }
                    ),

                    apiCall(
                        'dashboard/upcoming-maturities',
                        'GET',
                        null,
                        maturityParams
                    )
                ]);

            // =================================================
            // STATISTICS
            // =================================================

            const statsResult = results[0];

            if (
                statsResult.status === 'fulfilled' &&
                statsResult.value?.success
            ) {

                setStatistics(
                    statsResult.value.data || null
                );

            } else {

                setStatistics(null);

                console.error(
                    'Statistics API failed:',
                    statsResult.reason
                );
            }

            // =================================================
            // TRENDS
            // =================================================

            const trendsResult = results[1];

            if (
                trendsResult.status === 'fulfilled' &&
                trendsResult.value?.success
            ) {

                setTrends(
                    trendsResult.value.data || []
                );

            } else {

                setTrends([]);

                console.error(
                    'Trends API failed:',
                    trendsResult.reason
                );
            }

            // =================================================
            // BRANCH DATA
            // =================================================

            const branchResult = results[2];

            if (
                branchResult.status === 'fulfilled' &&
                branchResult.value?.success
            ) {

                setBranchData(
                    branchResult.value.data || []
                );

            } else {

                setBranchData([]);

                console.error(
                    'Branch API failed:',
                    branchResult.reason
                );
            }

            // =================================================
            // INSIGHTS
            // =================================================

            const insightsResult = results[3];

            if (
                insightsResult.status === 'fulfilled' &&
                insightsResult.value?.success
            ) {

                setInsights(
                    insightsResult.value.data || []
                );

            } else {

                setInsights([]);
            }

            // =================================================
            // TRANSACTIONS
            // =================================================

            const transactionsResult = results[4];

            if (
                transactionsResult.status === 'fulfilled' &&
                transactionsResult.value?.success
            ) {

                setRecentTransactions(
                    transactionsResult.value.data || []
                );

            } else {

                setRecentTransactions([]);
            }

            // =================================================
            // MATURITIES
            // =================================================

            const maturitiesResult = results[5];

            if (
                maturitiesResult.status === 'fulfilled' &&
                maturitiesResult.value?.success
            ) {

                setUpcomingMaturities(
                    maturitiesResult.value.data || []
                );

            } else {

                setUpcomingMaturities([]);
            }

        } catch (err) {

            console.error(
                'Dashboard error:',
                err
            );

            const message =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to load dashboard data';

            setError(message);

            toast.current?.show({
                severity: 'error',
                summary: 'Dashboard Error',
                detail: message,
                life: 5000
            });

        } finally {

            setLoading(false);
            setInitialLoading(false);
        }

    }, [apiCall, filters]);

    // =========================================================
    // INITIAL LOAD
    // =========================================================

    const initializeDashboard = useCallback(async () => {

        setInitialLoading(true);
        setError(null);

        try {

            await fetchFilterOptions();
            await fetchDashboardData();

        } catch (err) {

            console.error(
                'Initialization error:',
                err
            );

            setError(
                'Failed to initialize dashboard.'
            );

            setInitialLoading(false);
        }

    }, [
        fetchFilterOptions,
        fetchDashboardData
    ]);

    useEffect(() => {

        initializeDashboard();

    }, [initializeDashboard]);

    // =========================================================
    // FILTER CHANGE
    // =========================================================

    useEffect(() => {

        if (!initialLoading) {
            fetchDashboardData();
        }

    }, [
        filters.startDate,
        filters.endDate,
        filters.branchCode,
        filters.year
    ]);

    // =========================================================
    // FORMATTERS
    // =========================================================

    const formatCurrency = (value) => {

        if (
            value === null ||
            value === undefined ||
            isNaN(Number(value))
        ) {
            return '₹0';
        }

        return `₹${Number(value).toLocaleString(
            'en-IN',
            {
                maximumFractionDigits: 2
            }
        )}`;
    };

    const formatDate = (date) => {

        if (!date) {
            return '-';
        }

        const parsed = new Date(date);

        if (isNaN(parsed.getTime())) {
            return '-';
        }

        return parsed.toLocaleDateString(
            'en-IN',
            {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            }
        );
    };

    // =========================================================
    // ACCOUNT SEARCH
    // =========================================================

    const handleSearchAccount = async () => {

        const memberId =
            searchMemberId?.trim();

        if (!memberId) {

            toast.current?.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please enter a Member ID',
                life: 3000
            });

            return;
        }

        setSearchLoading(true);

        try {

            const response =
                await apiCall(
                    `dashboard/account-summary/${encodeURIComponent(memberId)}`
                );

            if (response?.success) {

                setAccountSummary(
                    response.data
                );

                setShowAccountDialog(true);

            } else {

                toast.current?.show({
                    severity: 'error',
                    summary: 'Not Found',
                    detail:
                        response?.message ||
                        'Member not found',
                    life: 3000
                });
            }

        } catch (err) {

            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail:
                    err?.response?.data?.message ||
                    'Member not found',
                life: 3000
            });

        } finally {

            setSearchLoading(false);
        }
    };

    // =========================================================
    // REFRESH
    // =========================================================

    const handleRefresh = async () => {

        await fetchDashboardData();

        toast.current?.show({
            severity: 'info',
            summary: 'Refreshed',
            detail: 'Dashboard data refreshed successfully',
            life: 2000
        });
    };

    // =========================================================
    // KPI CARD
    // =========================================================

    const KPICard = ({
        title,
        value,
        subtitle,
        icon,
        color,
        delay
    }) => {

        return (
            <div
                className="kpi-card"
                style={{
                    animationDelay: `${delay}ms`
                }}
            >
                <div
                    className="kpi-card-inner"
                    style={{
                        background: color.gradient,
                        boxShadow: color.shadow
                    }}
                >

                    <div className="kpi-card-header">

                        <div className="kpi-icon-wrapper">
                            <i
                                className={`pi ${icon} kpi-icon`}
                            />
                        </div>

                    </div>

                    <div className="kpi-card-body">

                        <div className="kpi-value">
                            {value}
                        </div>

                        <div className="kpi-title">
                            {title}
                        </div>

                        {subtitle && (
                            <div className="kpi-subtitle">
                                {subtitle}
                            </div>
                        )}

                    </div>

                </div>
            </div>
        );
    };

    // =========================================================
    // KPI CARDS
    // =========================================================

    const renderKPICards = () => {

        if (!statistics) {

            return (
                <div className="kpi-grid">

                    {[1, 2, 3, 4, 5, 6].map(
                        (item) => (
                            <Card
                                key={item}
                                className="kpi-skeleton"
                            >
                                <Skeleton
                                    width="100%"
                                    height="120px"
                                    borderRadius="16px"
                                />
                            </Card>
                        )
                    )}

                </div>
            );
        }

        const {
            overall = {},
            fixedDeposits = {},
            recurringDeposits = {},
            savingsAccounts = {}
        } = statistics;

        const colors = [

            {
                gradient:
                    'linear-gradient(135deg,#667eea,#764ba2)',
                shadow:
                    '0 10px 30px rgba(102,126,234,.35)'
            },

            {
                gradient:
                    'linear-gradient(135deg,#f093fb,#f5576c)',
                shadow:
                    '0 10px 30px rgba(245,87,108,.35)'
            },

            {
                gradient:
                    'linear-gradient(135deg,#4facfe,#00f2fe)',
                shadow:
                    '0 10px 30px rgba(79,172,254,.35)'
            },

            {
                gradient:
                    'linear-gradient(135deg,#43e97b,#38f9d7)',
                shadow:
                    '0 10px 30px rgba(67,233,123,.35)'
            },

            {
                gradient:
                    'linear-gradient(135deg,#fa709a,#fee140)',
                shadow:
                    '0 10px 30px rgba(250,112,154,.35)'
            },

            {
                gradient:
                    'linear-gradient(135deg,#a18cd1,#fbc2eb)',
                shadow:
                    '0 10px 30px rgba(161,140,209,.35)'
            }
        ];

        const cards = [

            {
                title: 'Total Deposits',
                value:
                    formatCurrency(
                        overall.totalDepositAmount
                    ),
                subtitle:
                    `${overall.totalDepositAccounts || 0} Accounts`,
                icon: 'pi-chart-line'
            },

            {
                title: 'Fixed Deposits',
                value:
                    formatCurrency(
                        fixedDeposits.totalAmount
                    ),
                subtitle:
                    `${fixedDeposits.activeAccounts || 0} Active`,
                icon: 'pi-money-bill'
            },

            {
                title: 'Recurring Deposits',
                value:
                    formatCurrency(
                        recurringDeposits.totalAmount
                    ),
                subtitle:
                    `${recurringDeposits.activeAccounts || 0} Active`,
                icon: 'pi-calendar'
            },

            {
                title: 'Savings Balance',
                value:
                    formatCurrency(
                        savingsAccounts.totalBalance
                    ),
                subtitle:
                    `${savingsAccounts.totalAccounts || 0} Accounts`,
                icon: 'pi-credit-card'
            },

            {
                title: 'Interest Earned',
                value:
                    formatCurrency(
                        overall.totalInterestEarned
                    ),
                subtitle: 'Total Interest',
                icon: 'pi-percentage'
            },

            {
                title: 'Active Accounts',
                value:
                    overall.activeAccounts || 0,
                subtitle: 'Total Active',
                icon: 'pi-users'
            }
        ];

        return (
            <div className="kpi-grid">

                {cards.map((card, index) => (

                    <KPICard
                        key={index}
                        {...card}
                        color={colors[index]}
                        delay={index * 100}
                    />

                ))}

            </div>
        );
    };

    // =========================================================
    // DEPOSIT DISTRIBUTION
    // =========================================================

    const renderDepositDistributionChart = () => {

        const dataList =
            statistics?.typeDistribution || [];

        const hasData =
            dataList.length > 0;

        const data = {

            labels: hasData
                ? dataList.map(
                    x => x.depositeType || 'Unknown'
                )
                : ['No Data'],

            datasets: [
                {
                    data: hasData
                        ? dataList.map(
                            x => x.totalAmount || 0
                        )
                        : [1],

                    backgroundColor: [
                        '#667eea',
                        '#f5576c',
                        '#4facfe',
                        '#43e97b'
                    ],

                    borderWidth: 2
                }
            ]
        };

        const options = {

            responsive: true,

            maintainAspectRatio: false,

            plugins: {

                legend: {
                    position: 'bottom'
                }
            },

            cutout: '65%'
        };

        return (
            <Card className="chart-card">

                <div className="chart-header">

                    <h3>
                        📊 Deposit Distribution
                    </h3>

                    <Tag
                        value={
                            hasData
                                ? 'Current'
                                : 'No Data'
                        }
                        severity={
                            hasData
                                ? 'success'
                                : 'warning'
                        }
                    />

                </div>

                <div style={{ height: '320px' }}>

                    <Chart
                        type="doughnut"
                        data={data}
                        options={options}
                    />

                </div>

            </Card>
        );
    };

    // =========================================================
    // MONTHLY TRENDS
    // =========================================================

    const renderMonthlyTrendsChart = () => {

        const hasData =
            trends.length > 0;

        const data = {

            labels: hasData
                ? trends.map(x => x.month)
                : [],

            datasets: [

                {
                    label: 'Fixed Deposits',

                    data: trends.map(
                        x => x.fixedDepositAmount || 0
                    ),

                    borderColor: '#667eea',
                    backgroundColor:
                        'rgba(102,126,234,.12)',

                    fill: true,
                    tension: 0.4
                },

                {
                    label: 'Recurring Deposits',

                    data: trends.map(
                        x => x.recurringDepositAmount || 0
                    ),

                    borderColor: '#f5576c',
                    backgroundColor:
                        'rgba(245,87,108,.12)',

                    fill: true,
                    tension: 0.4
                },

                {
                    label: 'Savings Balance',

                    data: trends.map(
                        x => x.savingsBalance || 0
                    ),

                    borderColor: '#4facfe',
                    backgroundColor:
                        'rgba(79,172,254,.12)',

                    fill: true,
                    tension: 0.4
                }
            ]
        };

        const options = {

            responsive: true,
            maintainAspectRatio: false,

            plugins: {
                legend: {
                    position: 'bottom'
                }
            },

            scales: {

                y: {

                    beginAtZero: true,

                    ticks: {
                        callback: (value) =>
                            `₹${Number(value).toLocaleString('en-IN')}`
                    }
                }
            }
        };

        return (
            <Card className="chart-card">

                <div className="chart-header">

                    <h3>
                        📈 Monthly Performance
                    </h3>

                    <Tag
                        value={
                            hasData
                                ? `${filters.year}`
                                : 'No Data'
                        }
                        severity={
                            hasData
                                ? 'success'
                                : 'warning'
                        }
                    />

                </div>

                <div style={{ height: '350px' }}>

                    <Chart
                        type="line"
                        data={data}
                        options={options}
                    />

                </div>

            </Card>
        );
    };

    // =========================================================
    // BRANCH PERFORMANCE
    // =========================================================

    const renderBranchPerformanceChart = () => {

        const hasData =
            branchData.length > 0;

        const data = {

            labels: hasData
                ? branchData.map(
                    x => x.branchName || x.branchCode
                )
                : ['No Branches'],

            datasets: [

                {
                    label: 'Fixed Deposits',

                    data: hasData
                        ? branchData.map(
                            x => x.fixedDepositAmount || 0
                        )
                        : [0],

                    backgroundColor:
                        'rgba(102,126,234,.75)'
                },

                {
                    label: 'Recurring Deposits',

                    data: hasData
                        ? branchData.map(
                            x => x.recurringDepositAmount || 0
                        )
                        : [0],

                    backgroundColor:
                        'rgba(245,87,108,.75)'
                },

                {
                    label: 'Savings Balance',

                    data: hasData
                        ? branchData.map(
                            x => x.savingsBalance || 0
                        )
                        : [0],

                    backgroundColor:
                        'rgba(79,172,254,.75)'
                }
            ]
        };

        const options = {

            responsive: true,
            maintainAspectRatio: false,

            plugins: {
                legend: {
                    position: 'bottom'
                }
            },

            scales: {

                y: {

                    beginAtZero: true,

                    ticks: {
                        callback: (value) =>
                            `₹${Number(value).toLocaleString('en-IN')}`
                    }
                }
            }
        };

        return (
            <Card className="chart-card">

                <div className="chart-header">

                    <h3>
                        🏢 Branch Performance
                    </h3>

                    <Tag
                        value={
                            hasData
                                ? `${branchData.length} Branches`
                                : 'No Data'
                        }
                        severity="info"
                    />

                </div>

                <div style={{ height: '350px' }}>

                    <Chart
                        type="bar"
                        data={data}
                        options={options}
                    />

                </div>

            </Card>
        );
    };

    // =========================================================
    // RECENT TRANSACTIONS
    // =========================================================

    const renderRecentTransactions = () => {

        const amountTemplate = (row) => {

            const debit =
                Number(row.debit || 0);

            const credit =
                Number(row.credit || 0);

            if (debit > 0) {

                return (
                    <span
                        style={{
                            color: '#f5576c',
                            fontWeight: 600
                        }}
                    >
                        − {formatCurrency(debit)}
                    </span>
                );
            }

            return (
                <span
                    style={{
                        color: '#43a047',
                        fontWeight: 600
                    }}
                >
                    + {formatCurrency(credit)}
                </span>
            );
        };

        return (
            <Card className="transactions-card">

                <div className="transactions-header">

                    <h3>
                        🔄 Recent Transactions
                    </h3>

                    <Badge
                        value={`${recentTransactions.length} entries`}
                        severity="info"
                    />

                </div>

                <DataTable
                    value={recentTransactions}
                    className="p-datatable-sm"
                    responsiveLayout="scroll"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50]}
                    emptyMessage="No recent transactions found"
                >

                    <Column
                        field="transactionid"
                        header="Transaction ID"
                        sortable
                    />

                    <Column
                        field="policyno"
                        header="Policy No"
                        sortable
                    />

                    <Column
                        field="transactiondate"
                        header="Date"
                        body={(row) =>
                            formatDate(
                                row.transactiondate
                            )
                        }
                        sortable
                    />

                    <Column
                        field="remarks"
                        header="Remarks"
                    />

                    <Column
                        header="Amount"
                        body={amountTemplate}
                        sortable
                    />

                    <Column
                        field="branchcode"
                        header="Branch"
                    />

                    <Column
                        field="empcode"
                        header="Employee"
                    />

                    <Column
                        header="Status"
                        body={() => (
                            <Tag
                                value="Completed"
                                severity="success"
                                rounded
                            />
                        )}
                    />

                </DataTable>

            </Card>
        );
    };

    // =========================================================
    // UPCOMING MATURITIES
    // =========================================================

    const renderUpcomingMaturities = () => {

        if (!upcomingMaturities.length) {
            return null;
        }

        return (
            <>
                <div
                    className="maturity-summary"
                    onClick={() =>
                        setShowMaturityDialog(true)
                    }
                >

                    <Card className="maturity-card">

                        <div className="maturity-content">

                            <div className="maturity-icon">
                                ⏰
                            </div>

                            <div>

                                <div className="maturity-count">
                                    {upcomingMaturities.length}
                                </div>

                                <div className="maturity-label">
                                    Deposits maturing
                                    in next 30 days
                                </div>

                            </div>

                            <i className="pi pi-chevron-right" />

                        </div>

                    </Card>

                </div>

                <Dialog
                    header="📋 Upcoming Maturities"
                    visible={showMaturityDialog}
                    onHide={() =>
                        setShowMaturityDialog(false)
                    }
                    style={{
                        width: '90%',
                        maxWidth: '1200px'
                    }}
                >

                    <DataTable
                        value={upcomingMaturities}
                        className="p-datatable-sm"
                        responsiveLayout="scroll"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[
                            10,
                            20,
                            50
                        ]}
                        emptyMessage="No upcoming maturities"
                    >

                        <Column
                            field="policyno"
                            header="Policy No"
                            sortable
                        />

                        <Column
                            field="memberid"
                            header="Member ID"
                            sortable
                        />

                        <Column
                            field="deposite_type"
                            header="Type"
                            sortable
                        />

                        <Column
                            field="amount"
                            header="Amount"
                            body={(row) =>
                                formatCurrency(row.amount)
                            }
                            sortable
                        />

                        <Column
                            field="maturityamount"
                            header="Maturity Amount"
                            body={(row) =>
                                formatCurrency(
                                    row.maturityamount
                                )
                            }
                            sortable
                        />

                        <Column
                            field="maturitydate"
                            header="Maturity Date"
                            body={(row) =>
                                formatDate(
                                    row.maturitydate
                                )
                            }
                            sortable
                        />

                        <Column
                            field="branchcode"
                            header="Branch"
                        />

                        <Column
                            field="interest"
                            header="Interest"
                            body={(row) =>
                                formatCurrency(
                                    row.interest
                                )
                            }
                        />

                    </DataTable>

                </Dialog>
            </>
        );
    };

    // =========================================================
    // INSIGHTS
    // =========================================================

    const renderInsights = () => {

        if (!insights.length) {

            return (
                <Card className="insights-card">

                    <div className="insights-header">
                        <h3>
                            💡 Key Insights
                        </h3>
                    </div>

                    <div className="no-data">
                        No insights available
                    </div>

                </Card>
            );
        }

        return (
            <Card className="insights-card">

                <div className="insights-header">

                    <h3>
                        💡 Key Insights
                    </h3>

                    <Badge
                        value={`${insights.length} insights`}
                        severity="info"
                    />

                </div>

                <div className="insights-grid">

                    {insights.map(
                        (insight, index) => (

                            <div
                                key={index}
                                className="insight-item"
                            >

                                <div className="insight-icon">
                                    {insight.icon || '📊'}
                                </div>

                                <div className="insight-content">

                                    <h4>
                                        {insight.title}
                                    </h4>

                                    <p>
                                        {insight.description}
                                    </p>

                                    {insight.trend && (
                                        <span
                                            className={
                                                insight.trend === 'up'
                                                    ? 'insight-trend trend-up'
                                                    : 'insight-trend trend-down'
                                            }
                                        >
                                            {insight.trend === 'up'
                                                ? '↑'
                                                : '↓'
                                            }{' '}
                                            {Number(
                                                insight.trendValue || 0
                                            ).toFixed(1)}
                                            %
                                        </span>
                                    )}

                                </div>

                            </div>
                        )
                    )}

                </div>

            </Card>
        );
    };

    // =========================================================
    // ACCOUNT SEARCH
    // =========================================================

    const renderAccountSearch = () => {

        return (
            <div className="account-search">

                <div className="search-container">

                    <span className="p-input-icon-left">

                        <i className="pi pi-search" />

                        <InputText
                            placeholder="Search Member ID"
                            value={searchMemberId}
                            onChange={(e) =>
                                setSearchMemberId(
                                    e.target.value
                                )
                            }
                            onKeyDown={(e) => {

                                if (
                                    e.key === 'Enter'
                                ) {
                                    handleSearchAccount();
                                }

                            }}
                        />

                    </span>

                    <Button
                        label="Search"
                        icon="pi pi-search"
                        onClick={
                            handleSearchAccount
                        }
                        loading={searchLoading}
                    />

                </div>

                <Dialog
                    header="👤 Account Summary"
                    visible={showAccountDialog}
                    onHide={() =>
                        setShowAccountDialog(false)
                    }
                    style={{
                        width: '90%',
                        maxWidth: '1100px'
                    }}
                >

                    {accountSummary && (

                        <div className="account-summary">

                            <div className="summary-header">

                                <div>

                                    <h3>
                                        Member:{' '}
                                        {
                                            accountSummary.memberId
                                        }
                                    </h3>

                                    <p>
                                        {
                                            accountSummary
                                                .savingsAccount
                                                ?.membername ||
                                            'Member'
                                        }
                                    </p>

                                </div>

                                <Tag
                                    value={`${accountSummary.activeDeposits || 0} Active Deposits`}
                                    severity="success"
                                    rounded
                                />

                            </div>

                            <div className="summary-stats">

                                <div className="stat-box">

                                    <div className="stat-label">
                                        Total Deposits
                                    </div>

                                    <div className="stat-value">
                                        {
                                            accountSummary
                                                .totalDeposits || 0
                                        }
                                    </div>

                                </div>

                                <div className="stat-box">

                                    <div className="stat-label">
                                        Deposit Amount
                                    </div>

                                    <div className="stat-value">
                                        {formatCurrency(
                                            accountSummary
                                                .totalDepositAmount
                                        )}
                                    </div>

                                </div>

                                <div className="stat-box">

                                    <div className="stat-label">
                                        Maturity Amount
                                    </div>

                                    <div className="stat-value">
                                        {formatCurrency(
                                            accountSummary
                                                .totalMaturityAmount
                                        )}
                                    </div>

                                </div>

                                <div className="stat-box">

                                    <div className="stat-label">
                                        Savings Balance
                                    </div>

                                    <div className="stat-value">
                                        {formatCurrency(
                                            accountSummary
                                                .savingsAccount
                                                ?.currentBalance
                                        )}
                                    </div>

                                </div>

                            </div>

                            {accountSummary.savingsAccount && (

                                <div className="savings-details">

                                    <h4>
                                        🏦 Savings Account
                                    </h4>

                                    <div className="details-grid">

                                        <div>
                                            <strong>
                                                Account No:
                                            </strong>{' '}
                                            {
                                                accountSummary
                                                    .savingsAccount
                                                    .accountnumber
                                            }
                                        </div>

                                        <div>
                                            <strong>
                                                Member Name:
                                            </strong>{' '}
                                            {
                                                accountSummary
                                                    .savingsAccount
                                                    .membername
                                            }
                                        </div>

                                        <div>
                                            <strong>
                                                Mobile:
                                            </strong>{' '}
                                            {
                                                accountSummary
                                                    .savingsAccount
                                                    .mobile
                                            }
                                        </div>

                                        <div>
                                            <strong>
                                                Email:
                                            </strong>{' '}
                                            {
                                                accountSummary
                                                    .savingsAccount
                                                    .emailid ||
                                                '-'
                                            }
                                        </div>

                                        <div>
                                            <strong>
                                                Branch:
                                            </strong>{' '}
                                            {
                                                accountSummary
                                                    .savingsAccount
                                                    .branchcode
                                            }
                                        </div>

                                        <div>
                                            <strong>
                                                Open Date:
                                            </strong>{' '}
                                            {formatDate(
                                                accountSummary
                                                    .savingsAccount
                                                    .accountopen_date
                                            )}
                                        </div>

                                        <div>
                                            <strong>
                                                Available Balance:
                                            </strong>{' '}
                                            {formatCurrency(
                                                accountSummary
                                                    .savingsAccount
                                                    .availableBalance
                                            )}
                                        </div>

                                    </div>

                                </div>
                            )}

                            {accountSummary.depositDetails?.length > 0 && (

                                <div className="deposit-details">

                                    <h4>
                                        📊 Deposit Details
                                    </h4>

                                    <DataTable
                                        value={
                                            accountSummary
                                                .depositDetails
                                        }
                                        className="p-datatable-sm"
                                        paginator
                                        rows={5}
                                        responsiveLayout="scroll"
                                    >

                                        <Column
                                            field="policyno"
                                            header="Policy No"
                                        />

                                        <Column
                                            field="deposite_type"
                                            header="Type"
                                        />

                                        <Column
                                            field="amount"
                                            header="Amount"
                                            body={(row) =>
                                                formatCurrency(
                                                    row.amount
                                                )
                                            }
                                        />

                                        <Column
                                            field="maturityamount"
                                            header="Maturity"
                                            body={(row) =>
                                                formatCurrency(
                                                    row.maturityamount
                                                )
                                            }
                                        />

                                        <Column
                                            field="term_period"
                                            header="Term"
                                            body={(row) =>
                                                `${row.term_period} Years`
                                            }
                                        />

                                        <Column
                                            field="interest"
                                            header="Interest"
                                            body={(row) =>
                                                formatCurrency(
                                                    row.interest
                                                )
                                            }
                                        />

                                        <Column
                                            field="ac_closed"
                                            header="Status"
                                            body={(row) => (

                                                <Tag
                                                    value={
                                                        row.ac_closed
                                                    }
                                                    severity={
                                                        row.ac_closed ===
                                                        'ACTIVE'
                                                            ? 'success'
                                                            : 'danger'
                                                    }
                                                    rounded
                                                />

                                            )}
                                        />

                                    </DataTable>

                                </div>
                            )}

                        </div>
                    )}

                </Dialog>

            </div>
        );
    };

    // =========================================================
    // FILTER BAR
    // =========================================================

    const renderFilterBar = () => {

        const branchOptions = [

            {
                label: 'All Branches',
                value: ''
            },

            ...(filterOptions.branches || []).map(
                (branch) => ({
                    label:
                        branch.name ||
                        branch.code,

                    value:
                        branch.code
                })
            )
        ];

        const yearOptions =
            (filterOptions.years || []).map(
                year => ({
                    label: String(year),
                    value: year
                })
            );

        return (
            <div className="filter-bar">

                <div className="filter-title">

                    <i className="pi pi-sliders-h" />

                    <span>
                        Filters
                    </span>

                </div>

                <div className="filter-right">

                    <Calendar
                        value={filters.startDate}
                        onChange={(e) =>
                            setFilters(prev => ({
                                ...prev,
                                startDate: e.value
                            }))
                        }
                        showIcon
                        placeholder="Start Date"
                        dateFormat="dd/mm/yy"
                    />

                    <Calendar
                        value={filters.endDate}
                        onChange={(e) =>
                            setFilters(prev => ({
                                ...prev,
                                endDate: e.value
                            }))
                        }
                        showIcon
                        placeholder="End Date"
                        dateFormat="dd/mm/yy"
                    />

                    <Dropdown
                        value={filters.year}
                        options={yearOptions}
                        onChange={(e) =>
                            setFilters(prev => ({
                                ...prev,
                                year: e.value
                            }))
                        }
                        placeholder="Year"
                    />

                    <Dropdown
                        value={filters.branchCode}
                        options={branchOptions}
                        onChange={(e) =>
                            setFilters(prev => ({
                                ...prev,
                                branchCode: e.value
                            }))
                        }
                        placeholder="Branch"
                    />

                    <Button
                        label="Refresh"
                        icon="pi pi-refresh"
                        onClick={handleRefresh}
                        loading={loading}
                    />

                </div>

            </div>
        );
    };

    // =========================================================
    // LOADING
    // =========================================================

    if (initialLoading) {

        return (
            <div className="dashboard-wrapper">

                <div className="loading-container">

                    <ProgressSpinner
                        style={{
                            width: '70px',
                            height: '70px'
                        }}
                    />

                    <h2>
                        Loading Dashboard...
                    </h2>

                    <p>
                        Please wait while we
                        fetch your data
                    </p>

                </div>

            </div>
        );
    }

    // =========================================================
    // ERROR
    // =========================================================

    if (error) {

        return (
            <div className="dashboard-wrapper">

                <div className="error-container">

                    <div className="error-icon-wrapper">

                        <i className="pi pi-exclamation-triangle" />

                    </div>

                    <h2>
                        Unable to load dashboard
                    </h2>

                    <p>
                        {error}
                    </p>

                    <Button
                        label="Try Again"
                        icon="pi pi-refresh"
                        onClick={initializeDashboard}
                    />

                </div>

            </div>
        );
    }

    // =========================================================
    // MAIN UI
    // =========================================================

    return (

        <div className="dashboard-wrapper">

            <Toast ref={toast} />

            <div className="dashboard-container">

                {/* HEADER */}

                <div className="dashboard-header">

                    <div className="header-top">

                        <div className="header-left">

                            <div className="header-icon-wrapper">

                                <i className="pi pi-chart-bar header-icon" />

                            </div>

                            <div>

                                <h1 className="dashboard-title">
                                    Deposit Dashboard
                                </h1>

                                <p className="dashboard-subtitle">
                                    Real-time overview of
                                    deposit accounts,
                                    savings and maturities
                                </p>

                            </div>

                        </div>

                        <div className="header-right">

                            {renderAccountSearch()}

                            <div className="header-status">

                                <Tag
                                    value="Live"
                                    severity="success"
                                    rounded
                                />

                                <Badge
                                    value="Updated"
                                    severity="info"
                                />

                            </div>

                        </div>

                    </div>

                    <div className="header-bottom">

                        <div className="header-stats">

                            <div className="stat-item">

                                <i className="pi pi-calendar" />

                                {new Date().toLocaleDateString(
                                    'en-IN',
                                    {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                    }
                                )}

                            </div>

                            <span>|</span>

                            <div className="stat-item">

                                <i className="pi pi-bell" />

                                {upcomingMaturities.length}
                                {' '}
                                Maturities

                            </div>

                            <span>|</span>

                            <div className="stat-item">

                                <i className="pi pi-database" />

                                {statistics?.overall
                                    ?.totalDepositAccounts || 0}
                                {' '}
                                Deposit Accounts

                            </div>

                        </div>

                    </div>

                    {renderFilterBar()}

                </div>

                {/* KPI */}

                {renderKPICards()}

                {/* MATURITIES */}

                {renderUpcomingMaturities()}

                {/* TABS */}

                <TabView
                    className="chart-tabs"
                    activeIndex={activeTab}
                    onTabChange={(e) =>
                        setActiveTab(e.index)
                    }
                >

                    <TabPanel header="📊 Overview">

                        <div className="charts-grid">

                            {renderDepositDistributionChart()}

                            {renderMonthlyTrendsChart()}

                            {renderBranchPerformanceChart()}

                        </div>

                    </TabPanel>

                    <TabPanel header="🔄 Transactions">

                        {renderRecentTransactions()}

                    </TabPanel>

                    <TabPanel header="👤 Member Search">

                        {renderAccountSearch()}

                    </TabPanel>

                </TabView>

                {/* INSIGHTS */}

                {renderInsights()}

                {/* FOOTER */}

                <div className="dashboard-footer">

                    <span>
                        © {new Date().getFullYear()}
                        {' '}
                        MicrofinCloud.
                        All rights reserved.
                    </span>

                    <span>|</span>

                    <span>
                        Data refreshed:
                        {' '}
                        {new Date().toLocaleTimeString(
                            'en-IN'
                        )}
                    </span>

                </div>

            </div>

        </div>
    );
};

export default DepositDashboard;